import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import {
  RevisionChangeRow,
  RevisionSentenceDiff,
  RevisionSummary,
  RevisionTextSegment,
  diffRevisionSentences,
  revisionHeadline,
  revisionSideWithEllipsis,
  shortValueChange,
} from '@src/app/features/dashboard/commercial/pages/deal-detail/components/deal-briefing-tab/briefing-revision';

/** Antes e depois com as reticências das pontas já no lugar (frase igual cortada ou texto recortado). */
function withEllipsis(diff: RevisionSentenceDiff): {
  before: RevisionTextSegment[];
  after: RevisionTextSegment[];
} {
  return {
    before: revisionSideWithEllipsis(diff.before, diff.trimmedStart, diff.trimmedEnd),
    after: revisionSideWithEllipsis(diff.after, diff.trimmedStart, diff.trimmedEnd),
  };
}

/** Linha de trecho já pronta para o template. */
interface RevisionRowView {
  row: RevisionChangeRow;
  /** Troca curta ao lado do nome ("R$ 8,1 MM → R$ 9,5 MM"); nula nas demais. */
  short: { before: string; after: string } | null;
  /** Antes e depois só da frase que mudou (troca de texto corrido), com as reticências das pontas. */
  diff: { before: RevisionTextSegment[]; after: RevisionTextSegment[] } | null;
}

/**
 * "O que mudou desde a v{n}" da versão aberta do Briefing (card 3878, Q11 opção B): fica ABAIXO da prévia, recolhido
 * numa linha de resumo ("Card Folga de captação (número): R$ 8,1 MM para R$ 9,5 MM · 4 trechos atualizados"). Aberto,
 * lista os trechos com o nome de negócio do lugar; cada trecho abre o antes e o depois só da frase que mudou, com as
 * palavras trocadas em destaque discreto. Regeneração inteira mostra só "Versão regerada por inteiro: {motivo}".
 *
 * ⚠️ Motivo, antes e depois vêm do agente, sem escape: o template só interpola (`{{ }}`), lista e objeto vão em
 * `<pre>`. Nunca `[innerHTML]`, pipe de markdown nem `bypassSecurityTrust*` aqui.
 */
@Component({
  selector: 'app-briefing-revision-summary',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './briefing-revision-summary.component.html',
  styleUrl: './briefing-revision-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingRevisionSummaryComponent {
  readonly summary = input.required<RevisionSummary>();

  /** Lista de trechos aberta (recolhida por padrão). */
  protected readonly expanded = signal(false);
  /** Trecho aberto na lista (o primeiro, como no protótipo); nulo fecha todos. */
  protected readonly openIndex = signal<number | null>(0);

  protected readonly headline = computed(() => revisionHeadline(this.summary()));

  protected readonly rows = computed<RevisionRowView[]>(() =>
    this.summary().rows.map(row => ({
      row,
      short: shortValueChange(row),
      diff:
        row.kind === 'changed' &&
        row.before &&
        row.after &&
        !row.before.structured &&
        !row.after.structured
          ? withEllipsis(diffRevisionSentences(row.before.text, row.after.text))
          : null,
    }))
  );

  protected readonly previousLabel = computed(() => {
    const previous = this.summary().previousNumber;
    return previous ? `v${previous}` : 'versão anterior';
  });

  protected toggle(): void {
    this.expanded.update(value => !value);
  }

  protected toggleRow(index: number): void {
    this.openIndex.update(current => (current === index ? null : index));
  }
}
